import { useReactiveVar } from '@apollo/client'
import { Box, Container } from '@material-ui/core'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { projectVar, selectedVersionsVar } from '../../apollo/reactive-vars'
import { mapVersionToCalculatedVersion } from '../../calculators/use-calculator'
import { getEmissionModel } from '../../emission-coefficients/emission-coefficients'
import { isNotNull } from '../../util/array-utility'
import {
  emissionUnit,
  formatEmissionPerYear,
  formatLargeEmission,
  largeEmissionUnit,
} from '../../util/formatters'
import { EnergyCalculationMissing } from '../common/EnergyCalculationMissing'
import { PageBreak } from '../common/PageBreak'
import { ReportHeader } from '../common/ReportHeader'
import { Section } from '../common/Section'
import { TableType } from './carbon-footprint-tables/carbon-footprint-table-util'
import CoefficientsReportTable from './carbon-footprint-tables/CoefficientsReportTable'
import FixedEmissionsTable from './carbon-footprint-tables/FixedEmissionsTable'
import TotalCarbonFootprintReportTable from './carbon-footprint-tables/TotalCarbonFootprintReportTable'
import { useReportStyles } from './use-report-styles'

export const CarbonFootprintReport = forwardRef<HTMLDivElement>(
  (props, ref): React.ReactElement => {
    const { t } = useTranslation()
    const classes = useReportStyles()
    const project = useReactiveVar(projectVar)
    const selectedVersions = useReactiveVar(selectedVersionsVar)

    if (!project) {
      return <></>
    }

    const calculatedVersions = selectedVersions.map((version) =>
      mapVersionToCalculatedVersion(version, version.energyCalculator),
    )
    if (calculatedVersions.some((version) => version === null)) {
      return <EnergyCalculationMissing />
    }
    const versions = calculatedVersions.filter(isNotNull)

    const emissionModel = getEmissionModel(project)

    return (
      <Container className={classes.printContainer} ref={ref}>
        <ReportHeader
          title={project?.name ?? ''}
          subtitle1={t('carbonFootprint.title')}
          subtitle2={t('carbonFootprint.tabs.results')}
        />
        <Section title={t('costs.versions')}>
          {selectedVersions.map((version) => (
            <Box key={version.id}>{version.name}</Box>
          ))}
        </Section>
        <TotalCarbonFootprintReportTable
          versions={versions}
          emissionModel={emissionModel}
          projectName={project?.name ?? ''}
        />
        <PageBreak />
        <ReportHeader
          title={project?.name ?? ''}
          subtitle1={t('carbonFootprint.title')}
          subtitle2=""
        />
        <CoefficientsReportTable emissionModel={emissionModel} />
        <PageBreak />
        <ReportHeader
          title={project?.name ?? ''}
          subtitle1={t('carbonFootprint.title')}
          subtitle2={t('common.breakdown')}
        />
        <div className={classes.avoidPageBreak}>
          <Section title={t('carbonFootprint.table.oneTime')} printPadding>
            <FixedEmissionsTable
              project={project}
              selectedVersions={selectedVersions}
              tableType={TableType.OneTime}
              formatter={formatLargeEmission}
              unit={largeEmissionUnit}
              createPrefilledEmission={undefined}
              updateEmission={undefined}
            />
          </Section>
        </div>
        <div className={classes.avoidPageBreak}>
          <Section title={t('carbonFootprint.table.annual')} printPadding>
            <FixedEmissionsTable
              project={project}
              selectedVersions={selectedVersions}
              tableType={TableType.Recurring}
              formatter={formatEmissionPerYear}
              unit={emissionUnit}
              createPrefilledEmission={undefined}
              updateEmission={undefined}
            />
          </Section>
        </div>
      </Container>
    )
  },
)
